// Bandeja de Atención a Clientes de una categoría (`tickets_categorias.bandeja_atencion`,
// migración 20261019183417). Solo tiene sentido en el pipeline de la app de clientes
// (`app_clientes`): decide si el caso lo ve Cobranza, Productos o ninguna de las dos. La BD solo
// deja cambiarla a un super administrador (trigger trg_tickets_categorias_guarda_bandeja).

export type BandejaAtencionCategoria = "cobranza" | "productos";

/** Valor del selector para "sin bandeja" (Radix Select no acepta "" como valor). */
export const SIN_BANDEJA = "ninguna";

export const OPCIONES_BANDEJA: { value: BandejaAtencionCategoria | typeof SIN_BANDEJA; label: string }[] = [
  { value: SIN_BANDEJA, label: "Ninguna" },
  { value: "cobranza", label: "Cobranza" },
  { value: "productos", label: "Productos" },
];

export const PIPELINE_APP_CLIENTES = "app_clientes";

/** El selector de bandeja solo aparece en el pipeline de la app de clientes. */
export function pipelineUsaBandeja(codigo: string | null | undefined): boolean {
  return (codigo ?? "").trim() === PIPELINE_APP_CLIENTES;
}

/** De la columna al valor del selector. Cualquier valor desconocido se trata como "Ninguna". */
export function valorSelectorBandeja(bandeja: string | null | undefined): BandejaAtencionCategoria | typeof SIN_BANDEJA {
  return bandeja === "cobranza" || bandeja === "productos" ? bandeja : SIN_BANDEJA;
}

/** Del selector a la columna: "Ninguna" se guarda como NULL. */
export function bandejaDesdeSelector(valor: string): BandejaAtencionCategoria | null {
  return valor === "cobranza" || valor === "productos" ? valor : null;
}

export function etiquetaBandeja(bandeja: string | null | undefined): string {
  return OPCIONES_BANDEJA.find((o) => o.value === valorSelectorBandeja(bandeja))?.label ?? "Ninguna";
}

/**
 * Error al guardar la bandeja en una frase. 42501 = el trigger (no es super admin): su mensaje ya
 * viene en español. 23514 = el CHECK de la columna.
 */
export function mensajeErrorBandeja(error: { code?: string; message?: string } | null | undefined): string {
  if (error?.code === "42501") {
    return error.message?.trim() || "Solo un super administrador puede cambiar la bandeja de Atención a clientes.";
  }
  if (error?.code === "23514") return "La bandeja tiene que ser Cobranza, Productos o Ninguna.";
  return `No se pudo guardar la bandeja. ${error?.message ?? ""}`.trim();
}

/**
 * ¿Cambiar la categoría de este ticket lo mete, lo saca o lo cambia de bandeja de Atención a
 * Clientes? Solo en el pipeline de la app de clientes; dentro de la misma bandeja, o entre
 * categorías sin bandeja, sigue siendo un cambio normal. Si es true, el cambio va por
 * `atencion_reclasificar_caso` (migración 20261019224713): un UPDATE directo lo rechaza la BD.
 */
export function cruzaBandejaAtencion(
  codigoPipeline: string | null | undefined,
  bandejaOrigen: string | null | undefined,
  bandejaDestino: string | null | undefined,
): boolean {
  if (!pipelineUsaBandeja(codigoPipeline)) return false;
  return bandejaDesdeSelector(bandejaOrigen ?? "") !== bandejaDesdeSelector(bandejaDestino ?? "");
}

/**
 * Error de `atencion_reclasificar_caso` en una frase para el toast. 42501 (sin permiso) y 22023
 * (datos que no cuadran) ya vienen en español. Sin la RPC en el ambiente (PGRST202 / 42883) se
 * dice que falta la migración.
 */
export function mensajeErrorReclasificar(error: { code?: string; message?: string } | null | undefined): string {
  const code = error?.code ?? "";
  const mensaje = (error?.message ?? "").trim();
  if ((code === "42501" || code === "22023") && mensaje) return mensaje;
  if (code === "PGRST202" || code === "42883") {
    return "Este ambiente todavía no tiene el cambio de categoría de Atención a clientes (falta la migración 20261019224713).";
  }
  return `No se pudo cambiar la categoría. ${mensaje}`.trim();
}
